import * as React from "react";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AdvisorGapRulesTable from "./AdvisorGapRulesTable";
import AdvisorGapRulesTableSkeleton from "./AdvisorGapRulesTableSkeleton";
import AdvisorGapRulesFilterItems from "./AdvisorGapRulesFilterItems";
import PersistentErrorSnackbar from "../../../PersistentErrorSnackbar/PersistentErrorSnackbar";
import AdvisorCreateGapRule from "./AdvisorCreateGapRule";
import AdvisorEditGapRule from "./AdvisorEditGapRule";
import AdvisorDuplicateGapRule from "./AdvisorDuplicateGapRule";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";
import { AdvisorGapRule } from "../helpers/advisorStaticData";
import "../Advisor.css";

const AdvisorGapRules = () => {
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const gapRulesData: AdvisorGapRule[] = useSelector(
    (state: any) => state.advisor?.gapRulesData,
  );
  const userData = useSelector(selectUserData);
  // without this, rules can be viewed but none of their actions are offered
  const hasPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "businesse_advisor",
    "create_edit_delete_advisor_maps_&_gap_rules",
  );
  const [loading, setLoading] = React.useState(true);
  const [ruleTitle, setRuleTitle] = React.useState<string | null>(null);
  const [selectedTargetField, setSelectedTargetField] = React.useState<
    string[]
  >([]);
  const [selectedDestinationTable, setSelectedDestinationTable] =
    React.useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = React.useState<string[]>([]);
  const [openCreateRule, setOpenCreateRule] = React.useState(false);
  const [ruleToEdit, setRuleToEdit] = React.useState<AdvisorGapRule | null>(
    null,
  );
  const [ruleToDuplicate, setRuleToDuplicate] =
    React.useState<AdvisorGapRule | null>(null);

  React.useEffect(() => {
    const fetchLists = async () => {
      setLoading(true);
      try {
        await Promise.all([
          AdvisorMapsEndpointLogic.getGapRules(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getDestinationTables(oauthTokenHeader),
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshGapRules = async () => {
    setLoading(true);
    try {
      await AdvisorMapsEndpointLogic.getGapRules(oauthTokenHeader);
    } finally {
      setLoading(false);
    }
  };

  // rule titles drive their own search box, so they are listed separately
  const ruleTitleOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          (gapRulesData ?? [])
            .map((rule) => rule?.rule_title)
            .filter((title): title is string => Boolean(title)),
        ),
      ),
    [gapRulesData],
  );

  const filteredGapRules = React.useMemo(
    () =>
      (gapRulesData ?? []).filter((rule) => {
        if (ruleTitle && rule?.rule_title !== ruleTitle) return false;
        if (
          selectedTargetField.length > 0 &&
          !selectedTargetField.includes(rule?.target_field)
        ) {
          return false;
        }
        if (
          selectedDestinationTable.length > 0 &&
          !selectedDestinationTable.includes(rule?.destination_table as string)
        ) {
          return false;
        }
        if (
          selectedStatus.length > 0 &&
          !selectedStatus.includes(String(Boolean(rule?.is_active)))
        ) {
          return false;
        }
        return true;
      }),
    [
      gapRulesData,
      ruleTitle,
      selectedTargetField,
      selectedDestinationTable,
      selectedStatus,
    ],
  );

  return (
    <>
      <PersistentErrorSnackbar />
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          The derivation library maps assign from. Create a new rule or edit an
          existing one — a rule targets one metric on one destination table and
          is offered to maps when it is active and matches.
        </Typography>

        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
          <InfoOutlinedIcon color="info" fontSize="small" />
          <Typography
            variant="body2"
            sx={{ fontSize: "14px", color: "info.main", fontWeight: 500 }}
          >
            Click on a rule title to see more details.
          </Typography>
        </Stack>
      </Box>

      <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2, mb: 1 }}>
        <Autocomplete
          sx={{ width: "260px", flexShrink: 0 }}
          options={ruleTitleOptions}
          value={ruleTitle}
          loading={loading}
          onChange={(_, newValue) => setRuleTitle(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Rule Title"
              color="secondary"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          size="small"
        />

        <Box sx={{ flex: 1 }}>
          <AdvisorGapRulesFilterItems
            gapRulesData={gapRulesData}
            selectedTargetField={selectedTargetField}
            setSelectedTargetField={setSelectedTargetField}
            selectedDestinationTable={selectedDestinationTable}
            setSelectedDestinationTable={setSelectedDestinationTable}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            onClearFilters={() => setRuleTitle(null)}
          />
        </Box>

        <Button
          variant="outlined"
          color="secondary"
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          onClick={() => setOpenCreateRule(true)}
          data-tracker="955"
          disabled={!hasPermission}
        >
          Create new Rule
          <AddIcon sx={{ width: "20px", ml: "5px" }} />
        </Button>
      </Stack>

      {loading ? (
        <AdvisorGapRulesTableSkeleton />
      ) : (
        <AdvisorGapRulesTable
          gapRulesData={filteredGapRules}
          onEditRule={(gapRule) => setRuleToEdit(gapRule)}
          onDuplicateRule={(gapRule) => setRuleToDuplicate(gapRule)}
        />
      )}

      <AdvisorCreateGapRule
        open={openCreateRule}
        onClose={() => setOpenCreateRule(false)}
        onCreated={refreshGapRules}
      />

      <AdvisorEditGapRule
        open={Boolean(ruleToEdit)}
        gapRule={ruleToEdit}
        onClose={() => setRuleToEdit(null)}
        onUpdated={refreshGapRules}
      />

      <AdvisorDuplicateGapRule
        open={Boolean(ruleToDuplicate)}
        gapRule={ruleToDuplicate}
        onClose={() => setRuleToDuplicate(null)}
        onCreated={refreshGapRules}
      />
    </>
  );
};

export default AdvisorGapRules;
